<template>
    <div class="library-qa-status-filter" data-testid="library-qa-status-filter">
        <CollapsibleWidget>
            <template #name>Review Status</template>

            <CheckboxGroup :model-value="qaUserStatusFilterSelection">
                <Checkbox
                    :label="BannerQaUserStatusFilter.All"
                    @click.stop.prevent="setQaUserStatusFilter(FilterGroup.All, BannerQaUserStatusFilter.All)"
                >
                    {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.All] }}
                </Checkbox>
                <Checkbox
                    :label="FilterGroup.Approved"
                    @click.stop.prevent="
                        setQaUserStatusFilter(FilterGroup.Approved, BannerQaUserStatusFilter.IApproved, true)
                    "
                >
                    Approved
                </Checkbox>
                <div v-show="isApprovedSubmenuVisible" class="library-published-status-filter__sub-group">
                    <Checkbox
                        :label="BannerQaUserStatusFilter.IApproved"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.Approved, BannerQaUserStatusFilter.IApproved)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.IApproved] }}
                    </Checkbox>
                    <Checkbox
                        :label="BannerQaUserStatusFilter.Approved"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.Approved, BannerQaUserStatusFilter.Approved)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.Approved] }}
                    </Checkbox>
                </div>

                <Checkbox
                    :label="FilterGroup.Rejected"
                    @click.stop.prevent="
                        setQaUserStatusFilter(FilterGroup.Rejected, BannerQaUserStatusFilter.IRejected, true)
                    "
                >
                    Rejected
                </Checkbox>
                <div v-show="isRejectedSubmenuVisible" class="library-published-status-filter__sub-group">
                    <Checkbox
                        :label="BannerQaUserStatusFilter.IRejected"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.Rejected, BannerQaUserStatusFilter.IRejected)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.IRejected] }}
                    </Checkbox>
                    <Checkbox
                        :label="BannerQaUserStatusFilter.Rejected"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.Rejected, BannerQaUserStatusFilter.Rejected)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.Rejected] }}
                    </Checkbox>
                </div>

                <Checkbox
                    :label="FilterGroup.NotReviewed"
                    @click.stop.prevent="
                        setQaUserStatusFilter(FilterGroup.NotReviewed, BannerQaUserStatusFilter.INone, true)
                    "
                >
                    Not reviewed
                </Checkbox>
                <div v-show="isNotReviewedSubmenuVisible" class="library-published-status-filter__sub-group">
                    <Checkbox
                        :label="BannerQaUserStatusFilter.INone"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.NotReviewed, BannerQaUserStatusFilter.INone)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.INone] }}
                    </Checkbox>
                    <Checkbox
                        :label="BannerQaUserStatusFilter.None"
                        @click.stop.prevent="
                            setQaUserStatusFilter(FilterGroup.NotReviewed, BannerQaUserStatusFilter.None)
                        "
                    >
                        {{ BannerQaUserStatusFilterLabel[BannerQaUserStatusFilter.None] }}
                    </Checkbox>
                </div>
            </CheckboxGroup>
        </CollapsibleWidget>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import { BannerQaUserStatusFilter, BannerQaUserStatusFilterLabel } from "@/enums/filters";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

const FilterGroup = {
    All: "All",
    Approved: "Approved",
    NotReviewed: "NotReviewed",
    Rejected: "Rejected"
};
const defaultSelection = {
    [FilterGroup.All]: BannerQaUserStatusFilter.All,
    [FilterGroup.Approved]: null,
    [FilterGroup.Rejected]: null,
    [FilterGroup.NotReviewed]: null
};

const approvedFilterStatuses = [
    BannerQaUserStatusFilter.Approved,
    BannerQaUserStatusFilter.IApproved,
    FilterGroup.Approved
];
const rejectedFilterStatuses = [
    BannerQaUserStatusFilter.Rejected,
    BannerQaUserStatusFilter.IRejected,
    FilterGroup.Rejected
];
const notReviewedFilterStatuses = [
    BannerQaUserStatusFilter.None,
    BannerQaUserStatusFilter.INone,
    FilterGroup.NotReviewed
];

const creativeLibraryFiltersStore = useCreativeLibraryFiltersStore();

const qaUserStatusFilterMap = computed({
    get() {
        const selectedFilters = creativeLibraryFiltersStore.reviewStatus;

        if (!selectedFilters.length) {
            return { ...defaultSelection };
        }

        return selectedFilters.reduce<
            Record<
                string,
                "ALL" | "NONE" | "QA_APPROVED" | "QA_REJECTED" | "INONE" | "IQA_APPROVED" | "IQA_REJECTED" | null
            >
        >(
            (acc, filter) => {
                if (isApprovedValue(filter)) {
                    return {
                        ...acc,
                        [FilterGroup.Approved]: filter,
                        [FilterGroup.All]: null
                    };
                }

                if (isRejectedValue(filter)) {
                    return {
                        ...acc,
                        [FilterGroup.Rejected]: filter,
                        [FilterGroup.All]: null
                    };
                }

                if (isNotReviewedValue(filter)) {
                    return {
                        ...acc,
                        [FilterGroup.NotReviewed]: filter,
                        [FilterGroup.All]: null
                    };
                }

                return acc;
            },
            { ...defaultSelection }
        );
    },
    set(
        value: Record<
            string,
            "ALL" | "NONE" | "QA_APPROVED" | "QA_REJECTED" | "INONE" | "IQA_APPROVED" | "IQA_REJECTED" | null
        >
    ) {
        const values = Object.values(value).filter(value => value !== null);
        creativeLibraryFiltersStore.reviewStatus = values;
    }
});

const isApprovedSubmenuVisible = computed(() => {
    return !!qaUserStatusFilterMap.value[FilterGroup.Approved];
});
const isNotReviewedSubmenuVisible = computed(() => {
    return !!qaUserStatusFilterMap.value[FilterGroup.NotReviewed];
});

const isRejectedSubmenuVisible = computed(() => {
    return !!qaUserStatusFilterMap.value[FilterGroup.Rejected];
});

const qaUserStatusFilterSelection = computed(() => {
    return [
        ...Object.keys(qaUserStatusFilterMap.value).filter(key => !!qaUserStatusFilterMap.value[key]),
        ...Object.values(qaUserStatusFilterMap.value).filter(Boolean)
    ];
});

function isApprovedValue(val: string) {
    if (Array.isArray(val)) {
        return val.some(v => approvedFilterStatuses.includes(v));
    }

    return approvedFilterStatuses.includes(val);
}

function isNotReviewedValue(val: string) {
    if (Array.isArray(val)) {
        return val.some(v => notReviewedFilterStatuses.includes(v));
    }

    return notReviewedFilterStatuses.includes(val);
}

function isRejectedValue(val: string) {
    if (Array.isArray(val)) {
        return val.some(v => rejectedFilterStatuses.includes(v));
    }

    return rejectedFilterStatuses.includes(val);
}

function setQaUserStatusFilter(
    group: string,
    qaStatus: "ALL" | "NONE" | "QA_APPROVED" | "QA_REJECTED" | "INONE" | "IQA_APPROVED" | "IQA_REJECTED",
    entireGroup = false
) {
    if (group === FilterGroup.All) {
        qaUserStatusFilterMap.value = { ...defaultSelection, [group]: qaStatus };
        return;
    }

    if (entireGroup && !!qaUserStatusFilterMap.value[group]) {
        qaUserStatusFilterMap.value = { ...qaUserStatusFilterMap.value, [group]: null };
        return;
    }

    if (qaUserStatusFilterMap.value[group] === qaStatus) {
        qaUserStatusFilterMap.value = { ...qaUserStatusFilterMap.value, [group]: null };
        return;
    }

    qaUserStatusFilterMap.value = { ...qaUserStatusFilterMap.value, [group]: qaStatus, [FilterGroup.All]: null };
}
</script>

<style lang="scss">
@use "@style/variables" as *;

.library-qa-status-filter {
    .ivu-radio {
        margin: 0;
    }

    .ivu-radio-group {
        display: block;
    }

    .ivu-radio-wrapper {
        margin: 0;
        padding: 0 0 0 50px;
        display: block;
    }
}

.library-qa-status-filter__status-dot-wrapper {
    display: inline-block;
    padding: 0 0 0 $spacing-smaller;
}
</style>
